<script lang="ts">
	//Svelte hooks
	import { format } from 'svelte-i18n';
	//Helpers
	import {
		get_gen_2_component_channel_structure,
		hasTilt,
		real_id,
	} from 'src/helpers/interface_helpers';
	//Stores
	import { devices } from 'src/stores/lists/devices';
	import { os } from 'src/stores/session/native-bridge';
	import { getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	//Design system components
	import {
		DeviceButton,
		DeviceButtonV2,
		DoubleDeviceButton,
		DoubleDeviceButtonV2,
		TripleDeviceButton,
		TripleDeviceButtonV2,
		type DeviceButtonV2PropsTypes,
		type DoubleDeviceButtonV2PropsTypes,
		type TripleDeviceButtonV2PropsTypes,
	} from '@Shelly/shelly-design-system';
	//Types
	import type { Device } from 'src/types/Device';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import { cloud_disabled_devices } from 'src/stores/device-runtime/firmware-ui';
	import { pin_unlocked_devices } from 'src/stores/device-runtime/pin-unlocked';
	import { hapticFeedback } from 'src/services/native_container_requests_ios';
	import { unlockPin } from 'src/helpers/pin_lock_helpers';

	import PinUnlock from 'src/components/PinUnlock.svelte';
	import { roller_is_calibrated } from 'src/helpers/requests/calibrate_cover';
	import { hasControlRights } from 'src/helpers/user_helpers';

	let className = '';
	export { className as class };

	$: settingsStore = getDeviceSettings(device.id);
	export let status, device: Device;
	export let useV2Components = false;
	export let devBtnsV2Props: {
		deviceButtonV2?: Partial<DeviceButtonV2PropsTypes>;
		doubleDeviceButtonV2?: Partial<DoubleDeviceButtonV2PropsTypes>;
		tripleDeviceButtonV2?: Partial<TripleDeviceButtonV2PropsTypes>;
	} = {
		deviceButtonV2: { size: 's' },
		doubleDeviceButtonV2: { size: 's' },
		tripleDeviceButtonV2: { size: 's' },
	};

	let isLoading = false;
	let showModal = false;

	let direction;
	let callbackValue;

	let btn1 = false;
	let btn2 = false;

	$: hasRights = hasControlRights(device);

	$: pinLocked =
		typeof $pin_unlocked_devices[device.id] === 'undefined' && $devices[device.id]?.pin_lock;

	$: {
		direction = status?.['cover:' + device.channel]?.state;
		btn1 = status?.['cover:' + device.channel]?.state === 'opening';
		btn2 = status?.['cover:' + device.channel]?.state === 'closing';
	}
	$: rollerCalibrated = roller_is_calibrated(device, status, $settingsStore);
	$: rollerLostPosition =
		status?.[`${get_gen_2_component_channel_structure(device)}`]?.current_pos === null;
	$: hasSlat =
		hasTilt(device) &&
		$settingsStore?.[get_gen_2_component_channel_structure(device)]?.slat?.enable &&
		rollerCalibrated &&
		!rollerLostPosition; //using to hide open blinds button

	const handleRequestGen2 = async (dir) => {
		if ((dir === 1 && direction === 'opening') || (dir === 0 && direction === 'closing')) {
			direction = 'stopped';
			if ($cloud_disabled_devices[device.id]) {
				btn1 = false;
				btn2 = false;
			}
		} else {
			direction = dir === 1 ? 'opening' : 'closing';
			if ($cloud_disabled_devices[device.id]) {
				btn1 = direction === 'opening';
				btn2 = direction === 'closing';
			}
		}

		isLoading = true;

		const method =
			direction === 'opening'
				? 'cover.open'
				: direction === 'closing'
					? 'cover.close'
					: 'cover.stop';

		const res: SettingRequest = await gen2Request(device, method, {
			id: device.channel,
		});
		// add error handle here
		if (res.isok) {
			status['cover:' + device.channel] = {
				res,
				...status['cover:' + device.channel],
			};

			devices_status.update_device_status({
				id: real_id(device.id),
				msg: status,
			});
		} else {
			console.error('Device control failed');
		}

		isLoading = false;
	};

	const handleRequest = (dir) => {
		if (!pinLocked) {
			if ($os === 'IOS') {
				hapticFeedback('buttonPressed');
			}
			handleRequestGen2(dir);
		} else {
			callbackValue = dir;
			showModal = true;
		}
	};

	const handlePin = () => {
		pinLocked = unlockPin(device, pinLocked);
		handleRequest(callbackValue);
		callbackValue = undefined;
	};
</script>

{#if hasSlat}
	{#if useV2Components}
		<TripleDeviceButtonV2
			aria-label={$format('label_direction', { default: 'Direction' })}
			isActive={btn1 || btn2}
			{isLoading}
			class={className}
			disabled={!hasRights}
			{...devBtnsV2Props?.tripleDeviceButtonV2}
		>
			<DeviceButtonV2
				id="devicebutton-tm877di"
				slot="buttonLeft"
				icon={'fa fa-arrow-up'}
				isActive={false}
				aria-label={$format('label_direction_up', { default: 'Direction Up' })}
				on:click={(e) => {
					e.stopPropagation();
					handleRequest(1);
				}}
				{...devBtnsV2Props?.deviceButtonV2}
			/>
			<DeviceButtonV2
				id="devicebutton-vsfv35r"
				slot="buttonMiddle"
				icon={'fa fa-pause'}
				isActive={false}
				aria-label={$format('label_direction_pause', { default: 'Direction Pause' })}
				on:click={(e) => {
					e.stopPropagation();
					direction = 'opening';
					handleRequest(1); //small cheat to stop cover
				}}
				{...devBtnsV2Props?.deviceButtonV2}
			/>
			<DeviceButtonV2
				id="devicebutton-cdos5nl"
				slot="buttonRight"
				icon={'fa fa-arrow-down'}
				isActive={false}
				aria-label={$format('label_direction_down', { default: 'Direction Down' })}
				on:click={(e) => {
					e.stopPropagation();
					handleRequest(0);
				}}
				{...devBtnsV2Props?.deviceButtonV2}
			/>
		</TripleDeviceButtonV2>
	{:else}
		<TripleDeviceButton
			aria-label={$format('label_direction', { default: 'Direction' })}
			isActive={btn1 || btn2}
			{isLoading}
			class={className}
			disabled={!hasRights}
		>
			<DeviceButton
				id="devicebutton-tm877di"
				slot="buttonLeft"
				icon={'fa fa-arrow-up'}
				aria-label={$format('label_direction_up', { default: 'Direction Up' })}
				on:click={(e) => {
					e.stopPropagation();
					handleRequest(1);
				}}
			/>
			<DeviceButton
				id="devicebutton-vsfv35r"
				slot="buttonMiddle"
				icon={'fa fa-pause'}
				aria-label={$format('label_direction_pause', { default: 'Direction Pause' })}
				on:click={(e) => {
					e.stopPropagation();
					direction = 'opening';
					handleRequest(1); //small cheat to stop cover
				}}
			/>
			<DeviceButton
				id="devicebutton-cdos5nl"
				slot="buttonRight"
				icon={'fa fa-arrow-down'}
				aria-label={$format('label_direction_down', { default: 'Direction Down' })}
				on:click={(e) => {
					e.stopPropagation();
					handleRequest(0);
				}}
			/>
		</TripleDeviceButton>
	{/if}
{:else if useV2Components}
	<DoubleDeviceButtonV2
		id="doubledevicebutton-udjh4kr"
		aria-label={$format('label_direction', { default: 'Direction' })}
		isActive={btn1 || btn2}
		{isLoading}
		class={className}
		disabled={!hasRights}
		{...devBtnsV2Props?.doubleDeviceButtonV2}
	>
		<DeviceButtonV2
			id="devicebutton-8srvcag"
			slot="buttonLeft"
			icon={btn1 ? 'fa fa-pause' : 'fa fa-arrow-up'}
			isActive={false}
			aria-label={$format('label_direction_up_or_pause', { default: 'Direction Up or Pause' })}
			aria-pressed={btn1}
			on:click={(e) => {
				e.stopPropagation();
				handleRequest(1);
			}}
			{...devBtnsV2Props?.deviceButtonV2}
		/>
		<DeviceButtonV2
			id="devicebutton-otzycmb"
			slot="buttonRight"
			icon={btn2 ? 'fa fa-pause' : 'fa fa-arrow-down'}
			isActive={false}
			aria-label={$format('label_direction_down_or_pause', { default: 'Direction Down or Pause' })}
			aria-pressed={btn2}
			on:click={(e) => {
				e.stopPropagation();
				handleRequest(0);
			}}
			{...devBtnsV2Props?.deviceButtonV2}
		/>
	</DoubleDeviceButtonV2>
{:else}
	<DoubleDeviceButton
		id="doubledevicebutton-udjh4kr"
		aria-label={$format('label_direction', { default: 'Direction' })}
		isActive={btn1 || btn2}
		{isLoading}
		class={className}
		disabled={!hasRights}
	>
		<DeviceButton
			id="devicebutton-8srvcag"
			slot="buttonLeft"
			icon={btn1 ? 'fa fa-pause' : 'fa fa-arrow-up'}
			aria-label={$format('label_direction_up_or_pause', { default: 'Direction Up or Pause' })}
			aria-pressed={btn1}
			on:click={(e) => {
				e.stopPropagation();
				handleRequest(1);
			}}
		/>
		<DeviceButton
			id="devicebutton-otzycmb"
			slot="buttonRight"
			icon={btn2 ? 'fa fa-pause' : 'fa fa-arrow-down'}
			aria-label={$format('label_direction_down_or_pause', { default: 'Direction Down or Pause' })}
			aria-pressed={btn2}
			on:click={(e) => {
				e.stopPropagation();
				handleRequest(0);
			}}
		/>
	</DoubleDeviceButton>
{/if}

{#if showModal}
	<PinUnlock bind:showModal on:unlock={handlePin} on:cancel={() => (showModal = false)} />
{/if}
